import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    effect,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { QuickDepositConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollFreezeService } from '@frontend/sports/common/core/feature/scroll-freeze';
import { SmoothScroll } from '@frontend/sports/common/core/feature/smooth-scroll';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { WINDOW } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { BetslipQdMessageService } from '@payments/betslipqd-feature';
import { Observable, distinctUntilChanged, distinctUntilKeyChanged, filter, map } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { QuickDepositActions } from '../quick-deposit-actions';
import { QuickDepositPage, QuickDepositSource } from '../quick-deposit.models';
import { IQuickDepositState } from '../quick-deposit.state';
import { quickDepositSelector } from '../selector';
import { QuickDepositService } from '../services/quick-deposit.service';
import { defaultQuickDepositAnimationConfig } from './quick-deposit-animation.directive';
import { QuickDepositTrackingService } from './quick-deposit-tracking.service';

@Component({
    selector: 'bs-quick-deposit',
    templateUrl: 'quick-deposit.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-deposit.component.scss'],
    standalone: false,
})
export class QuickDepositComponent implements OnInit, OnDestroy {
    @Input() inColumn = false;
    @Input() keepOpen = false;
    @Input() trackingSource: QuickDepositSource;

    quickDepositState$: Observable<IQuickDepositState>;

    url: SafeResourceUrl = '';
    quickDepositTitle = '';
    animationConfig?: typeof defaultQuickDepositAnimationConfig;

    @HostBinding('class') class = 'quick-deposit__container';
    @HostBinding('class.quick-deposit__loading') loading = true;

    private quickDepositWindow: Window;
    private quickDepositIFrame: HTMLIFrameElement;
    private quickDepositPage: QuickDepositPage;
    private unsubscribeGlobalClick: () => void;
    private unsubscribeCashierEvents: () => void;
    private unsubscribeWindowResize: () => void;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _window = inject(WINDOW);

    constructor(
        private quickDepositService: QuickDepositService,
        private elementRef: ElementRef,
        private smoothScrollService: SmoothScroll,
        private renderer: Renderer2,
        private scrollContainerService: ScrollContainerService,
        private changeDetector: ChangeDetectorRef,
        public sitecore: Sitecore,
        private scrollFreezeService: ScrollFreezeService,
        private trackingService: QuickDepositTrackingService,
        private sanitizer: DomSanitizer,
        private store: Store<IBetslipRootState>,
        private quickDepositConfig: QuickDepositConfig,
        private betslipQdMessageService: BetslipQdMessageService,
    ) {
        this.quickDepositPage = QuickDepositPage.Deposit;
        this.quickDepositState$ = this.store.select(quickDepositSelector);

        if (!this.quickDepositConfig.isPaymentsLibraryEnabled) {
            this.animationConfig = defaultQuickDepositAnimationConfig;
        }

        effect(
            () => {
                const outputMessage = this.betslipQdMessageService.output();
                if (outputMessage.eventName) {
                    this.quickDepositService.handleCashierMessage({ data: outputMessage } as MessageEvent);
                }
            },
            { allowSignalWrites: true },
        );
    }

    ngOnInit(): void {
        this.setTitle();
        if (!this.quickDepositConfig.isPaymentsLibraryEnabled) {
            this.url = this.sanitizer.bypassSecurityTrustResourceUrl(this.quickDepositService.getUrl());
        }
        this.createSubscriptions();
    }

    ngOnDestroy(): void {
        this.quickDepositService.close(false);
        this.removeSubscription();
    }

    dispatchVisible(visible: boolean): void {
        this.store.dispatch(QuickDepositActions.quickDepositVisible({ visible }));
    }

    onIFrameLoad(event: Event): void {
        this.quickDepositIFrame = event.target as HTMLIFrameElement;
        // load event occurs twice since iframe is created dynamically,
        // with this check we detect first call and skip handling
        if (!this.quickDepositIFrame) {
            return;
        }

        this.quickDepositIFrame.height = `0px`;
        if (this.quickDepositIFrame.src === '') {
            return;
        }
        this.tryFreezeScroll();
        this.quickDepositWindow = this.quickDepositIFrame.contentWindow!;
        this.quickDepositService.setCashierWindow(this.quickDepositWindow);
    }

    onAnimationDone(): void {
        if (!this.quickDepositService.isOpen) {
            this.dispatchVisible(false);

            return;
        }
        this.scrollIntoView();
    }

    close(): void {
        if (!this.keepOpen) {
            this.trackingService.trackClose(this.quickDepositPage, this.trackingSource);
            this.quickDepositService.close(true);
        }
    }

    get isPaymentsLibraryEnabled(): boolean {
        return this.quickDepositConfig.isPaymentsLibraryEnabled;
    }

    get currentStake(): any {
        return this.quickDepositService.currentStake;
    }

    private createSubscriptions(): void {
        this.unsubscribeCashierEvents = this.renderer.listen('window', 'message', this.quickDepositService.handleCashierMessage);
        this.unsubscribeGlobalClick = this.renderer.listen('document', 'mousedown', this.onGlobalClick);
        this.unsubscribeWindowResize = this.renderer.listen('window', 'resize', this.triggerAnimation);
        this.quickDepositState$
            .pipe(
                map((quickDepositState) => quickDepositState.isOpen),
                distinctUntilChanged(),
                filter((open) => !open),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((isOpen) => {
                this.quickDepositCloseHandler();
            });
        this.quickDepositState$
            .pipe(distinctUntilKeyChanged('page'), takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => this.quickDepositPageChangeHandler(value));
        this.quickDepositState$
            .pipe(distinctUntilKeyChanged('height'), takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => this.quickDepositResizeHandler(value));
        this.quickDepositState$
            .pipe(distinctUntilKeyChanged('deltaY'), takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => this.quickDepositScrollHandler(value));
    }

    private quickDepositCloseHandler(): void {
        this.removeSubscription();
        this.tryReleaseScroll();
        this.changeDetector.markForCheck();
    }

    private quickDepositResizeHandler(quickDepositState: any): void {
        const height = quickDepositState.height;
        if (height > this.animationConfig!.initialHeight) {
            this.loading = false;
        }
        this.triggerAnimation(height);
    }

    private triggerAnimation = (newHeight?: number) => {
        this.animationConfig = {
            ...defaultQuickDepositAnimationConfig,
            startHeight: this.quickDepositIFrame?.clientHeight,
            endHeight: newHeight!,
            inColumn: this.inColumn,
        };
        this.changeDetector.markForCheck();
    };

    private quickDepositScrollHandler(quickDepositState: any): void {
        if (this.inColumn) {
            this.scrollContainerService.scrollToDelta(0, quickDepositState.deltaY);
        }
    }

    private quickDepositPageChangeHandler(quickDepositState: any): void {
        this.quickDepositPage = quickDepositState.page;
        this.setTitle();
    }

    private setTitle(): void {
        switch (this.quickDepositPage) {
            case QuickDepositPage.Deposit:
                this.quickDepositTitle = this.sitecore.betslip.messages.QuickDepositTitle;
                break;
            case QuickDepositPage.Withdrawal:
                this.quickDepositTitle = this.sitecore.betslip.messages.QuickDepositWithdrawalTitle;
                break;
            default:
                throw new Error(`Deposit page type: ${this.quickDepositPage} not supported`);
        }

        this.changeDetector.markForCheck();
    }

    private onGlobalClick = (event: MouseEvent) => {
        const bounds = this.elementRef.nativeElement.parentElement.getBoundingClientRect();
        const inBounds = (x: number, y: number) => {
            return x >= bounds.x && y >= bounds.y && x < bounds.x + bounds.width && y < bounds.y + bounds.height;
        };

        if (!inBounds(event.x, event.y)) {
            this.close();
        }
    };

    private removeSubscription(): void {
        if (this.unsubscribeCashierEvents) {
            this.unsubscribeCashierEvents();
        }
        if (this.unsubscribeGlobalClick) {
            this.unsubscribeGlobalClick();
        }
        if (this.unsubscribeWindowResize) {
            this.unsubscribeWindowResize();
        }
    }

    private scrollIntoView(): void {
        if (!this.inColumn || !this.elementRef.nativeElement.parentElement) {
            return;
        }

        const container = this.scrollContainerService.container;
        const scrollContainerRect = container.getBoundingClientRect();
        const parentElementRect = this.elementRef.nativeElement.parentElement.getBoundingClientRect();
        const windowOverflow = scrollContainerRect.bottom - this._window.innerHeight;

        const deltaTop = parentElementRect.top - scrollContainerRect.top;
        let deltaBottom = parentElementRect.bottom - scrollContainerRect.bottom;
        if (windowOverflow > 0) {
            deltaBottom += windowOverflow;
        }

        if (deltaBottom > 0 && deltaTop < 0) {
            return;
        }

        if (deltaBottom > 0) {
            this.smoothScrollService.scrollTopTo(container, container.scrollTop + deltaBottom, this.animationConfig!.duration, true);
        }

        if (deltaTop < 0) {
            this.scrollContainerService.scrollToDelta(0, deltaTop);
        }
    }

    private tryFreezeScroll(): void {
        if (this.inColumn) {
            return;
        }

        if (!this.scrollFreezeService.isScrollFrozen) {
            this.scrollFreezeService.freezeScroll();
        }
    }

    private tryReleaseScroll(): void {
        if (this.scrollFreezeService.isScrollFrozen) {
            this.scrollFreezeService.releaseScroll();
        }
    }
}
